/**
 * DocumentReviewSendDialog — two-step draft → send lifecycle (D-Q5).
 *
 * Step 1 (Save Draft): reviewer selects recipients + adds note → "Save Draft".
 *   Review status stays 'draft'. No email dispatched.
 *
 * Step 2 (Send): reviewer revisits, optionally edits → "Send for Review".
 *   Review transitions draft → active. Mailgun emails dispatched per-recipient.
 *
 * Forked from submittals/SendForReviewDialog.tsx — uses the document_review
 * substrate instead of the legacy submittal_annotations path.
 * # Covers D-Q5 (draft→active lifecycle), D-Q11 (Mailgun per-recipient email)
 */
import { useState } from 'react';
import { Send, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface Engineer {
  id: string;
  name: string | null;
  email: string;
  organization_name?: string | null;
}

interface DocumentReviewSendDialogProps {
  open: boolean;
  onClose: () => void;
  documentReviewId: string;
  projectId: string;
  /** Current review status — used to show correct CTA label. */
  reviewStatus?: string;
}

export function DocumentReviewSendDialog({
  open,
  onClose,
  documentReviewId,
  projectId,
  reviewStatus = 'draft',
}: DocumentReviewSendDialogProps) {
  const queryClient = useQueryClient();
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch project scopes to resolve engineer contacts with emails
  const { data: scopesData, isLoading: scopesLoading } = useQuery({
    queryKey: ['project-scopes', projectId],
    queryFn: () => api.projectScopes.list(projectId) as Promise<{ scopes: any[] }>,
    enabled: open && !!projectId,
  });

  const engineers: Engineer[] = [];
  const seenIds = new Set<string>();

  if (scopesData?.scopes) {
    for (const scope of scopesData.scopes) {
      const contacts = scope.contacts || [];
      for (const c of contacts) {
        if (c.contact_id && c.contact_email && !seenIds.has(c.contact_id)) {
          seenIds.add(c.contact_id);
          engineers.push({
            id: c.contact_id,
            name: c.contact_name || null,
            email: c.contact_email,
            organization_name: scope.organization_name || null,
          });
        }
      }
    }
  }

  const toggleEngineer = (email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      // Draft save: just update review status to 'draft' (it already is, but
      // this call records the recipient selection for resumption). No email sent.
      await api.documentReviews.saveDraft(documentReviewId, {
        recipient_emails: Array.from(selectedEmails),
        note: note.trim() || undefined,
      });
      toast.success('Draft saved — no email sent yet');
      queryClient.invalidateQueries({ queryKey: ['document-review', documentReviewId] });
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (selectedEmails.size === 0) {
      toast.error('Select at least one recipient');
      return;
    }

    setSending(true);
    try {
      const result = await api.documentReviews.send(documentReviewId, {
        recipient_emails: Array.from(selectedEmails),
        note: note.trim() || undefined,
      });

      const sentCount = result.sent_to?.length || 0;
      toast.success(
        `Review sent to ${sentCount} recipient${sentCount !== 1 ? 's' : ''}`,
      );
      queryClient.invalidateQueries({ queryKey: ['document-review', documentReviewId] });
      setNote('');
      setSelectedEmails(new Set());
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to send review');
    } finally {
      setSending(false);
    }
  };

  const isDraft = reviewStatus === 'draft';
  const count = selectedEmails.size;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isDraft ? 'Save Draft / Send for Review' : 'Send for Review'}
          </DialogTitle>
          <DialogDescription>
            {isDraft
              ? 'Save a draft to return later, or send now to dispatch emails to each recipient.'
              : 'Send the annotated PDF to selected recipients.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Recipient list */}
          <div className="space-y-2">
            <Label>Recipients (from project scopes)</Label>
            {scopesLoading && (
              <p className="text-sm text-muted-foreground">Loading contacts…</p>
            )}
            {!scopesLoading && engineers.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No contacts with email addresses found in project scopes.
              </p>
            )}
            <div className="max-h-48 overflow-y-auto space-y-1 rounded border p-2">
              {engineers.map((eng) => (
                <label
                  key={eng.id}
                  className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={selectedEmails.has(eng.email)}
                    onChange={() => toggleEngineer(eng.email)}
                    className="h-3.5 w-3.5"
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium truncate block">
                      {eng.name || eng.email}
                    </span>
                    <span className="text-xs text-muted-foreground truncate block">
                      {eng.email}
                      {eng.organization_name ? ` · ${eng.organization_name}` : ''}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Optional note */}
          <div className="space-y-2">
            <Label htmlFor="dr-review-note">Note (optional)</Label>
            <Textarea
              id="dr-review-note"
              placeholder="Add a note for recipients…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {isDraft && (
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={saving}
            >
              <Save className="w-3 h-3 mr-1" />
              {saving ? 'Saving…' : 'Save Draft'}
            </Button>
          )}
          <Button
            onClick={handleSend}
            disabled={sending || count === 0 || scopesLoading}
          >
            <Send className="w-3 h-3 mr-1" />
            {sending
              ? 'Sending…'
              : `Send to ${count || '…'} recipient${count !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
